/**
 * Subscription Plans and Feature Gating
 * Manages user subscription tiers and feature access
 */

export type SubscriptionPlan = "free" | "pro" | "pro_plus" | "enterprise";

export interface PlanFeatures {
  // Claude API Features
  maxContextWindow: number; // tokens
  extendedThinking: boolean;
  toolUse: boolean;
  memory: boolean;
  webSearch: boolean;
  codeExecution: boolean;
  computerUse: boolean;
  filesApi: boolean;
  citations: boolean;
  structuredOutputs: boolean;
  batchProcessing: boolean;
  
  // Media Generation
  musicGeneration: boolean;
  videoGeneration: boolean;
  photoGeneration: boolean;
  
  // Chat Features
  karmaAcChat: boolean; // Live chat with Karma AC assistant
  voiceConversation: boolean; // Real-time voice-to-voice conversation
  
  // Usage Limits
  messagesPerDay: number;
  apiCallsPerDay: number;
  fileUploadsPerDay: number;
  mediaGenerationsPerMonth: number;
  
  // Other Features
  unlimitedTiles: boolean;
  customBranding: boolean;
  whiteLabel: boolean;
  teamCollaboration: boolean;
  prioritySupport: boolean;
}

export const PLAN_FEATURES: Record<SubscriptionPlan, PlanFeatures> = {
  free: {
    maxContextWindow: 200000, // 200k tokens
    extendedThinking: false,
    toolUse: true, // Basic tool use
    memory: false,
    webSearch: false,
    codeExecution: false,
    computerUse: false,
    filesApi: false,
    citations: false,
    structuredOutputs: false,
    batchProcessing: false,
    musicGeneration: false,
    videoGeneration: false,
    photoGeneration: false,
    karmaAcChat: false,
    voiceConversation: false,
    messagesPerDay: 100,
    apiCallsPerDay: 50,
    fileUploadsPerDay: 10,
    mediaGenerationsPerMonth: 0,
    unlimitedTiles: false,
    customBranding: false,
    whiteLabel: false,
    teamCollaboration: false,
    prioritySupport: false,
  },
  pro: {
    maxContextWindow: 1000000, // 1M tokens
    extendedThinking: true,
    toolUse: true,
    memory: true,
    webSearch: true,
    codeExecution: true,
    computerUse: false,
    filesApi: true,
    citations: true,
    structuredOutputs: true,
    batchProcessing: true,
    musicGeneration: true,
    videoGeneration: false,
    photoGeneration: true,
    karmaAcChat: true,
    voiceConversation: false,
    messagesPerDay: 1000,
    apiCallsPerDay: 500,
    fileUploadsPerDay: 100,
    mediaGenerationsPerMonth: 50,
    unlimitedTiles: true,
    customBranding: true,
    whiteLabel: false,
    teamCollaboration: false,
    prioritySupport: true,
  },
  pro_plus: {
    maxContextWindow: 1000000, // 1M tokens
    extendedThinking: true,
    toolUse: true,
    memory: true,
    webSearch: true,
    codeExecution: true,
    computerUse: true,
    filesApi: true,
    citations: true,
    structuredOutputs: true,
    batchProcessing: true,
    musicGeneration: true,
    videoGeneration: true,
    photoGeneration: true,
    karmaAcChat: true, // Top tier gets Karma AC chat
    voiceConversation: true, // Real-time voice-to-voice conversation
    messagesPerDay: 10000,
    apiCallsPerDay: 5000,
    fileUploadsPerDay: 1000,
    mediaGenerationsPerMonth: 200,
    unlimitedTiles: true,
    customBranding: true,
    whiteLabel: true,
    teamCollaboration: true,
    prioritySupport: true,
  },
  enterprise: {
    maxContextWindow: 1000000, // 1M tokens
    extendedThinking: true,
    toolUse: true,
    memory: true,
    webSearch: true,
    codeExecution: true,
    computerUse: true,
    filesApi: true,
    citations: true,
    structuredOutputs: true,
    batchProcessing: true,
    musicGeneration: true,
    videoGeneration: true,
    photoGeneration: true,
    karmaAcChat: true,
    voiceConversation: true,
    messagesPerDay: 100000,
    apiCallsPerDay: 50000,
    fileUploadsPerDay: 10000,
    mediaGenerationsPerMonth: 1000,
    unlimitedTiles: true,
    customBranding: true,
    whiteLabel: true,
    teamCollaboration: true,
    prioritySupport: true,
  },
};

/**
 * Get features for a subscription plan
 */
export function getPlanFeatures(plan: SubscriptionPlan): PlanFeatures {
  return PLAN_FEATURES[plan] || PLAN_FEATURES.free;
}

/**
 * Check if a feature is available for a plan
 */
export function hasFeature(
  plan: SubscriptionPlan,
  feature: keyof PlanFeatures
): boolean {
  const features = getPlanFeatures(plan);
  return features[feature] === true;
}

/**
 * Check if user can access a feature
 */
export function canAccessFeature(
  plan: SubscriptionPlan,
  feature: keyof PlanFeatures
): boolean {
  return hasFeature(plan, feature);
}

/**
 * Get upgrade message for a feature
 */
export function getUpgradeMessage(feature: string): string {
  return `This feature requires a Pro or higher subscription. Upgrade to unlock ${feature} and more premium features.`;
}

